import { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import type { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { AssessmentFilterOption } from "./assessmentFilterOption";
import {
  assessmentCenterFilterBarActionsSx,
  assessmentCenterFilterBarRootSx,
  assessmentCenterFilterBarScrollSx,
  assessmentCenterPlansLikeButtonSx,
} from "./assessmentCenterFilterBarSx";
import AssessmentCenterRefreshLatestStatusButton from "./AssessmentCenterRefreshLatestStatusButton";
import clipboardListCheckIcon from "../../assets/icon-clipboard-list-check.svg";
import { appDatePickerSlots } from "../shared/datePickerSlots";
import { getAssessmentFilterOptions } from "../../services/regulationsService";
import { getAssessmentCenterAiFilterOptions } from "../../services/assessmentCenterService";
import type { AssessmentFilterOptionsResponse } from "../../services/assessmentCenterService";

const IMPACT_LEVEL_OPTIONS: AssessmentFilterOption[] = [
  { label: "High", value: "High" },
  { label: "Medium", value: "Medium" },
  { label: "Low", value: "Low" },
  { label: "No", value: "No" },
];

function pickOptionValue(opt: {
  name: string;
  value: string | string[];
}): string {
  if (typeof opt.value === "string" && opt.value.trim()) return opt.value;
  return opt.name.trim().toLowerCase();
}

interface AiImpactAssessmentFilterBarProps {
  readonly onOpenPlanManagement?: () => void;
  readonly onRefresh?: () => void;
  readonly seedRegulationId?: string | null;
  readonly seedRegulationVersion?: string | null;
  readonly filterConfigsOverride?: AssessmentFilterOptionsResponse;

  readonly executedDate: Dayjs | null;
  readonly onExecutedDateChange: (next: Dayjs | null) => void;

  readonly status: string;
  readonly onStatusChange: (next: string) => void;

  readonly executedBy: string;
  readonly onExecutedByChange: (next: string) => void;

  readonly assessmentType: string;
  readonly onAssessmentTypeChange: (next: string) => void;

  readonly impactLevel: string;
  readonly onImpactLevelChange: (next: string) => void;
}

export default function AiImpactAssessmentFilterBar({
  onOpenPlanManagement,
  onRefresh,
  seedRegulationId,
  seedRegulationVersion,
  filterConfigsOverride,
  executedDate,
  onExecutedDateChange,
  status,
  onStatusChange,
  executedBy,
  onExecutedByChange,
  assessmentType,
  onAssessmentTypeChange,
  impactLevel,
  onImpactLevelChange,
}: AiImpactAssessmentFilterBarProps) {
  const handleExecutedDateChange = useCallback(
    (next: Dayjs | null) => {
      onExecutedDateChange(next);
    },
    [onExecutedDateChange],
  );

  const { data } = useQuery({
    queryKey: ["assessment-center", "ai", "filter-options"],
    queryFn: async () => {
      if (Array.isArray(filterConfigsOverride) && filterConfigsOverride.length) {
        return filterConfigsOverride;
      }
      try {
        return await getAssessmentCenterAiFilterOptions();
      } catch {
        if (seedRegulationId && seedRegulationVersion) {
          const res = await getAssessmentFilterOptions(
            seedRegulationId,
            seedRegulationVersion,
          );
          return Array.isArray(res.data) ? res.data : [];
        }
        return [];
      }
    },
    enabled: !(
      Array.isArray(filterConfigsOverride) && filterConfigsOverride.length > 0
    ),
    staleTime: 5 * 60 * 1000,
  });

  const [filterConfigs, setFilterConfigs] = useState<AssessmentFilterOptionsResponse>([]);
  useEffect(() => {
    const next =
      (Array.isArray(filterConfigsOverride) ? filterConfigsOverride : undefined) ??
      (Array.isArray(data) ? (data as unknown as AssessmentFilterOptionsResponse) : []);
    if (next.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilterConfigs(next);
    }
  }, [data, filterConfigsOverride]);

  const statusOptions: AssessmentFilterOption[] = useMemo(() => {
    const config = filterConfigs.find((f) => f.name === "status");
    const opts = config?.options ?? [];
    return opts.map((o) => ({ label: o.name, value: o.name }));
  }, [filterConfigs]);

  const executedByOptions: AssessmentFilterOption[] = useMemo(() => {
    const config = filterConfigs.find((f) => f.name === "executed_by");
    const opts = config?.options ?? [];
    return opts.map((o) => ({ label: o.name, value: pickOptionValue(o) }));
  }, [filterConfigs]);

  const assessmentTypeOptions: AssessmentFilterOption[] = useMemo(() => {
    const config = filterConfigs.find((f) => f.name === "by");
    const opts = config?.options ?? [];
    return opts.map((o) => ({ label: o.name, value: pickOptionValue(o) }));
  }, [filterConfigs]);

  const impactLevelOptions: AssessmentFilterOption[] = useMemo(() => {
    const config = filterConfigs.find((f) => f.name === "impacted_level");
    const opts = config?.options ?? [];
    if (opts.length > 0) {
      return opts.map((o) => ({ label: o.name, value: pickOptionValue(o) }));
    }
    return IMPACT_LEVEL_OPTIONS;
  }, [filterConfigs]);

  return (
    <Box sx={assessmentCenterFilterBarRootSx}>
      <Box sx={assessmentCenterFilterBarScrollSx}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Executed Date"
          value={executedDate}
          format="DD-MMM-YYYY"
          onChange={handleExecutedDateChange}
          slots={appDatePickerSlots}
          slotProps={{
            field: {
              clearable: true,
              onClear: () => handleExecutedDateChange(null),
            },
            textField: {
              size: "small",
              sx: { minWidth: 200 },
            },
          }}
        />
      </LocalizationProvider>

      <TextField
        select
        label="Status"
        value={status}
        onChange={(e) => onStatusChange(e.target.value)}
        sx={{ minWidth: 200 }}
        size="small"
      >
        <MenuItem value="">All</MenuItem>
        {statusOptions.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Executed by"
        value={executedBy}
        onChange={(e) => onExecutedByChange(e.target.value)}
        sx={{ minWidth: 200 }}
        size="small"
      >
        <MenuItem value="">All</MenuItem>
        {executedByOptions.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Assessment Type"
        value={assessmentType}
        onChange={(e) => onAssessmentTypeChange(e.target.value)}
        sx={{ minWidth: 200 }}
        size="small"
      >
        <MenuItem value="">All</MenuItem>
        {assessmentTypeOptions.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Impact Level"
        value={impactLevel}
        onChange={(e) => onImpactLevelChange(e.target.value)}
        sx={{ minWidth: 200 }}
        size="small"
      >
        <MenuItem value="">All</MenuItem>
        {impactLevelOptions.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      </Box>

      <Box sx={assessmentCenterFilterBarActionsSx}>
        {onOpenPlanManagement ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onOpenPlanManagement}
            startIcon={
              <Box
                component="img"
                src={clipboardListCheckIcon}
                alt=""
                sx={{
                  width: 20,
                  height: 20,
                  display: "block",
                  objectFit: "contain",
                }}
              />
            }
            sx={assessmentCenterPlansLikeButtonSx}
          >
            Assessment plans
          </Button>
        ) : null}
        <AssessmentCenterRefreshLatestStatusButton onRefresh={onRefresh} />
      </Box>
    </Box>
  );
}
